import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, KeyRound } from "lucide-react";
import { inputCls, btnPrimary, formatErr } from "@/components/admin/ui";
import { AlertsCard } from "@/components/admin/AnnouncementPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Card = ({ icon, eyebrow, title, children, testid }) => (
    <div data-testid={testid} className="rounded-2xl border border-amber-500/25 bg-zinc-900/60 backdrop-blur-md p-5 sm:p-7">
        <div className="flex items-center gap-2 text-amber-300">
            {icon}
            <p className="font-meta text-[10px] tracking-[0.3em] uppercase">{eyebrow}</p>
        </div>
        <h2 className="font-display text-lg font-bold text-white mt-2 mb-5">{title}</h2>
        {children}
    </div>
);

export const AccountPanel = ({ user, token, onEmailChanged, onTokenRefreshed }) => {
    const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
    const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
    const [savingEmail, setSavingEmail] = useState(false);
    const [savingPw, setSavingPw] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const submitEmail = async (e) => {
        e.preventDefault();
        setSavingEmail(true);
        try {
            const { data } = await axios.put(`${API}/auth/email`, emailForm, { headers });
            onTokenRefreshed(data.access_token);
            onEmailChanged(data.email);
            setEmailForm({ new_email: "", current_password: "" });
            toast.success(`Sign-in email is now ${data.email}.`);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not change the email."));
        } finally {
            setSavingEmail(false);
        }
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        setSavingPw(true);
        try {
            const { data } = await axios.put(`${API}/auth/password`, pwForm, { headers });
            onTokenRefreshed(data.access_token);
            setPwForm({ current_password: "", new_password: "", confirm_password: "" });
            toast.success("Password updated. Other devices have been signed out.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not change the password."));
        } finally {
            setSavingPw(false);
        }
    };

    return (
        <section data-testid="admin-account-tab" className="grid gap-6 md:grid-cols-2">
            <Card icon={<Mail size={16} />} eyebrow="Sign-in email" title="Change email" testid="account-email-card">
                <p className="text-sm text-zinc-400 mb-5">
                    Currently <span className="text-amber-200">{user.email}</span>. The new address becomes your login.
                </p>
                <form onSubmit={submitEmail} className="grid gap-3">
                    <input
                        required
                        type="email"
                        value={emailForm.new_email}
                        onChange={(e) => setEmailForm((f) => ({ ...f, new_email: e.target.value }))}
                        placeholder="New email address"
                        data-testid="account-new-email-input"
                        className={inputCls}
                    />
                    <input
                        required
                        type="password"
                        value={emailForm.current_password}
                        onChange={(e) => setEmailForm((f) => ({ ...f, current_password: e.target.value }))}
                        placeholder="Current password"
                        data-testid="account-email-password-input"
                        className={inputCls}
                    />
                    <button type="submit" disabled={savingEmail} data-testid="account-email-submit" className={`${btnPrimary} justify-self-start`}>
                        {savingEmail && <Loader2 size={13} className="animate-spin" />}
                        {savingEmail ? "Saving…" : "Update Email"}
                    </button>
                </form>
            </Card>

            <Card icon={<KeyRound size={16} />} eyebrow="Security" title="Change password" testid="account-password-card">
                <p className="text-sm text-zinc-400 mb-5">
                    At least 8 characters. Saving signs out every other device.
                </p>
                <form onSubmit={submitPassword} className="grid gap-3">
                    <input
                        required
                        type="password"
                        value={pwForm.current_password}
                        onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))}
                        placeholder="Current password"
                        data-testid="account-current-password-input"
                        className={inputCls}
                    />
                    <input
                        required
                        minLength={8}
                        type="password"
                        value={pwForm.new_password}
                        onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
                        placeholder="New password"
                        data-testid="account-new-password-input"
                        className={inputCls}
                    />
                    <input
                        required
                        minLength={8}
                        type="password"
                        value={pwForm.confirm_password}
                        onChange={(e) => setPwForm((f) => ({ ...f, confirm_password: e.target.value }))}
                        placeholder="Repeat new password"
                        data-testid="account-confirm-password-input"
                        className={inputCls}
                    />
                    <button type="submit" disabled={savingPw} data-testid="account-password-submit" className={`${btnPrimary} justify-self-start`}>
                        {savingPw && <Loader2 size={13} className="animate-spin" />}
                        {savingPw ? "Saving…" : "Update Password"}
                    </button>
                </form>
            </Card>

            <AlertsCard token={token} />
        </section>
    );
};

export default AccountPanel;
